import React, { useMemo } from "react";
import { Table } from "react-bootstrap";
import { useLanguage } from "./LanguageContext";
import CustomTable from "../helpers/customtable";

const numberFormatter = new Intl.NumberFormat("hu-HU");

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const number = Number(value);

  return Number.isFinite(number) ? numberFormatter.format(number) : value;
};

function CmrPreview({ data }) {
  const { t } = useLanguage();
  const calculateBandingByType = (parts) => {
    const result = {};
    parts.forEach((p) => {
      const len = Number(p.length);
      const wid = Number(p.width);
      const qty = Number(p.qty);
      const sides = [
        { type: p.kantL1, size: len },
        { type: p.kantL2, size: len },
        { type: p.kantW1, size: wid },
        { type: p.kantW2, size: wid }
      ];
      sides.forEach((s) => {
        if (!s.type || s.type === "-") return;
        if (!result[s.type]) result[s.type] = 0;
        result[s.type] += s.size * qty;
      });
    });
    return result;
  };

  const bandingSummary = useMemo(() => {
    const bandingByType = calculateBandingByType(data.parts);
    const wasteFactor = 1.1;
    return Object.entries(bandingByType).map(([type, length]) => ({
      type,
      raw: length,
      withWaste: Math.round((length * wasteFactor) / 100) * 100
    }));
  }, [data]);
  const partsRows = useMemo(
    () =>
      data.parts.map((part) => ({
        ...part,

        idLabel: formatNumber(part.id),
        lengthLabel: formatNumber(part.length),
        widthLabel: formatNumber(part.width),
        qtyLabel: formatNumber(part.qty),

        rotatableLabel: part.rotatable ? t("yes") : t("no"),
        commentLabel: part.comment || "-"
      })),
    [data.parts, t]
  );

  const partsTableConfig = useMemo(
    () => ({
      rowKey: "id",
      emptyText: t("none"),
      pageSize: 10,
      showPageSizeSelector: true,
      pageSizeOptions: [10, 25, 50, 100],
      showTotalCount: true,

      labels: {
        filter: t("filter"),
        search: t("search"),
        actions: t("actions")
      },

      columns: [
        {
          key: "id",
          header: "#",
          width: "5%",
          align: "right",
          sortValue: (row) => Number(row.id)
        },
        {
          key: "length",
          header: t("length"),
          width: "10%",
          align: "right",
          sortValue: (row) => Number(row.length)
        },
        {
          key: "width",
          header: t("width"),
          width: "10%",
          align: "right",
          sortValue: (row) => Number(row.width)
        },
        {
          key: "qty",
          header: t("quantityShort"),
          width: "6%",
          align: "right",
          sortValue: (row) => Number(row.qty)
        },
        {
          key: "rotatableLabel",
          header: t("rotable"),
          width: "10%",
          sortValue: (row) => (row.rotatable ? 1 : 0)
        },
        {
          key: "commentLabel",
          header: t("cmrComment"),
          width: "15%"
        },

        {
          key: "kantL1",
          header: "L1",
          width: "5%",
          sortable: false,
          filterable: false,
          default: "-"
        },
        {
          key: "kantL2",
          header: "L2",
          width: "5%",
          sortable: false,
          filterable: false,
          default: "-"
        },
        {
          key: "kantW1",
          header: "W1",
          width: "5%",
          sortable: false,
          filterable: false,
          default: "-"
        },
        {
          key: "kantW2",
          header: "W2",
          width: "5%",
          sortable: false,
          filterable: false,
          default: "-"
        }
      ]
    }),
    [t]
  );
  return (
    <>
      <h5 className="mb-3">{t("cmrStats")}</h5>
      <Table bordered size="sm" className="mb-4">
        <tbody>
          <tr>
            <td>{t("cmrTotalParts")}</td>
            <td>{formatNumber(data.stats?.parts)}</td>
          </tr>
          <tr>
            <td>{t("cmrUsedParts")}</td>
            <td>{formatNumber(data.stats?.usedParts)}</td>
          </tr>
          <tr>
            <td>{t("cmrUsedSheets")}</td>
            <td>{formatNumber(data.stats?.usedSheets)}</td>
          </tr>
          <tr>
            <td>{t("cmrYield")}</td>
            <td>{formatNumber(data.stats?.avgYield)}%</td>
          </tr>
          <tr>
            <td>{t("cmrLastYield")}</td>
            <td>{formatNumber(data.stats?.lastYield)}%</td>
          </tr>
          <tr>
            <td>{t("cmrCutLength")}</td>
            <td>{formatNumber(data.stats?.cutLength)} mm</td>
          </tr>
          <tr>
            <td>{t("cmrBandingLength")}</td>
            <td>{formatNumber(data.stats?.bandingLength)} mm</td>
          </tr>
        </tbody>
      </Table>

      <h5 className="mb-3">{t("cmrBandingByType")}</h5>
      <Table bordered size="sm" className="mb-4">
        <thead>
          <tr>
            <th>{t("cmrType")}</th>
            <th>{t("cmrLengthMm")}</th>
            <th>+10%</th>
          </tr>
        </thead>
        <tbody>
          {bandingSummary.map((b) => (
            <tr key={b.type}>
              <td>{b.type}</td>
              <td>{formatNumber(b.raw)}</td>
              <td>{formatNumber(b.withWaste)}</td>
            </tr>
          ))}
        </tbody>
      </Table>

      <h5 className="mb-3">{t("cmrSheets")}</h5>
      <Table striped bordered hover className="mb-4">
        <thead>
          <tr>
            <th>#</th>
            <th>{t("length")}</th>
            <th>{t("width")}</th>
            <th>{t("quantity")}</th>
            <th>{t("material")}</th>
          </tr>
        </thead>
        <tbody>
          {data.sheets.map((s) => (
            <tr key={s.id}>
              <td>{formatNumber(s.id)}</td>
              <td>{formatNumber(s.length)}</td>
              <td>{formatNumber(s.width)}</td>
              <td>{formatNumber(s.qty)}</td>
              <td>{s.material}</td>
            </tr>
          ))}
        </tbody>
      </Table>

      <h5 className="mb-3">{t("cmrParts")}</h5>

      <CustomTable data={partsRows} config={partsTableConfig} />
    </>
  );
}

export default CmrPreview;
